import { Platform } from 'react-native';

import { api } from './api';
import { showForcedUpdate } from './appUpdateGate';
import { getApiConfig } from '../config/api';
import { APP_UPDATE_ROUTE } from '../constants/appUpdate';
import { getAppVersion } from '../utils/appVersion';

export class AppUpdateRequiredError extends Error {
  constructor() {
    super('Update required');
    this.name = 'AppUpdateRequiredError';
  }
}

export type AppVersionPlatform = 'ios' | 'android';

export interface AppVersionCheckResponse {
  currentVersion: string;
  requiredVersion: string | null;
  updateRequired: boolean;
  storeUrl: string | null;
}

export function buildAppVersionCheckBody(
  platform: string,
  version: string,
): { version: string; platform: AppVersionPlatform } {
  if (platform !== 'ios' && platform !== 'android') {
    throw new Error('App version check runs on iOS and Android only');
  }
  const storeVersion = version.trim();
  if (storeVersion.length === 0) {
    throw new Error('Store version is required');
  }
  return { version: storeVersion, platform };
}

/**
 * POST /api/config/app-versions.
 * The api wrapper attaches a stored bearer, which records the check.
 * A call with no stored token compares only.
 */
export async function checkAppVersion(): Promise<AppVersionCheckResponse> {
  const body = buildAppVersionCheckBody(Platform.OS, getAppVersion());
  return api.post<AppVersionCheckResponse>('/config/app-versions', body);
}

export function forcedUpdatePageUrl(platform: AppVersionPlatform): string | null {
  const base = getApiConfig().WEB_BASE_URL.replace(/\/$/, '');
  if (!base) {
    return null;
  }
  return `${base}${APP_UPDATE_ROUTE}?platform=${platform}`;
}

/**
 * Behind the required store version opens the hosted update page.
 * Returns true when the phone must stay on that page.
 */
export function applyAppVersionCheckResponse(
  response: AppVersionCheckResponse,
): boolean {
  if (!response.updateRequired) {
    return false;
  }
  if (Platform.OS !== 'ios' && Platform.OS !== 'android') {
    return false;
  }
  const pageUrl = forcedUpdatePageUrl(Platform.OS);
  if (!pageUrl) {
    return false;
  }
  showForcedUpdate(pageUrl);
  return true;
}

/**
 * Native launch, Log In, and the call before auth status.
 * A failed check does not block. The website does not call the endpoint.
 */
export async function runAppVersionCheck(): Promise<'blocked' | 'continue'> {
  if (Platform.OS !== 'ios' && Platform.OS !== 'android') {
    return 'continue';
  }
  try {
    const response = await checkAppVersion();
    return applyAppVersionCheckResponse(response) ? 'blocked' : 'continue';
  } catch {
    return 'continue';
  }
}
